import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from '@modifi/plugin-i18n'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {Form} from '@modifi/plugin-forms'

import DirectorFormContent from '../components/director-form-content'
import PersonFormFooter from '../../components/person-form-footer'
import {ROUTE_COMPANY_PROFILE_DIRECTORS} from '../../../../routes/constants'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData, SelectOption} from '../../../../types/common'

export type CreateDirectorProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: (externalCompanyId: string) => void
  externalCompanyId: string
  formData: EntityFormData
  onSubmit: (changes: Record<string, unknown>, externalCompanyId: string) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  dataSources: SelectOption[]
  onSelectDataSource: (value: string) => void
  pageIsVerified: boolean
  hasAnyRoleBasedFieldsVisible: boolean
  redirect: (route: string) => void
}

const CreateDirector: React.FC<CreateDirectorProps> = ({
  updatePending,
  externalCompanyId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  dataSources,
  onSelectDataSource,
  pageIsVerified,
  hasAnyRoleBasedFieldsVisible,
  redirect,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)
  const [submitLabel, setSubmitLabel] = useState(t('sub-section-footer.cta-next') as string)

  useEffect(() => {
    initialize(externalCompanyId)
  }, [initialize, externalCompanyId])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  // setting submit button label when page changes
  useEffect(() => {
    if (currentPage === 1) {
      setSubmitLabel(t('sub-section-footer.cta-next'))
    } else if (currentPage === 2) {
      if (dataSources?.length > 0 && !hasAnyRoleBasedFieldsVisible) {
        setSubmitLabel(t('sub-section-footer.cta-submit'))
      } else if (dataSources?.length > 0 && hasAnyRoleBasedFieldsVisible) {
        setSubmitLabel(t('sub-section-footer.cta-next'))
      } else {
        setSubmitLabel(t('sub-section-footer.cta-submit'))
      }
    } else if (currentPage === 3) {
      setSubmitLabel(t('sub-section-footer.cta-submit'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleSubmit = useCallback(
    (changes: Record<string, any>) => {
      if (
        currentPage === 1 ||
        (currentPage === 2 && dataSources.length > 0 && hasAnyRoleBasedFieldsVisible)
      ) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes, externalCompanyId)
      }
    },
    [onSubmit, externalCompanyId, currentPage, dataSources.length, hasAnyRoleBasedFieldsVisible]
  )

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  if (isLoading) {
    return null
  }
  return (
    <CustomerUploadManager>
      {pageIsVerified && <SuccessAlert>{t('director-page.fully-verified-alert')}</SuccessAlert>}
      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row>
        <Col>
          <ButtonLink
            data-target="director-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_DIRECTORS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{t('director-create-page.heading')}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        defaultValues={formData.defaultValues}
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <DirectorFormContent
            t={t}
            isNewDirector
            selectPersonAsDataSource={onSelectDataSource}
            dataSourceOptions={dataSources}
            currentPage={currentPage}
            hasAnyRoleFieldItem={hasAnyRoleBasedFieldsVisible}
          />
        </FormContentWrapper>

        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={submitLabel}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={dataSources.length > 0 ? currentPage === 3 : currentPage === 2}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default CreateDirector
